import * as React from 'react';
import styled from 'styled-components';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers';
import * as yup from 'yup';
import { FormServerError } from '../../types';
import {
  ButtonWithLoader,
  InputBaseContainer,
  InputFileContainer,
  InputTextWithLabel,
  InputSelectWithLabel,
  CheckboxWithLabel,
} from '../../molecules';
import isYupFieldRequired from '../../utils/isYupFieldRequired';
import CommentsChat from '../../organisms/CommentsChat';
import PermitDocumentFormsTable from '../../organisms/PermitDocumentFormsTable';
import {
  getPermitDocumentComments,
  createPermitDocumentComment,
} from '../../api/projectDocuments';
import {
  COMMENTS_DATETIME_FORMAT,
  GET_ALL_QUERY_PARAMS,
  MAX_UPLOAD_FILE_SIZE,
  MAX_UPLOAD_FILE_SIZE_LABEL,
} from '../../constants';
import { formatDateTime } from '../../utils/formatDate';
import FilesBubbles from './FilesBubbles';
import ConfirmNoFilesDialog, { CrudType } from './ConfirmNoFilesDialog';

const labels = {
  documentName: 'Document Name',
  documentFile: 'File Attachment',
  status: 'Status',
  isCompleted: 'Completed',
  name: 'Name',
  forClient: "Received from Client",
};

const schema = yup.object().shape({
  documentName: yup
    .string()
    .matches(/^[^-\s]+/, 'Document Name is required')
    .required()
    .label(labels.documentName),
});

const schemaEdit = yup.object().shape({
  status: yup
    .object()
    .required('A status is required')
    .label(labels.status),
  isCompleted: yup.boolean().label(labels.isCompleted),
  name: yup.string().label(labels.name),
});

const PermitDocumentForm = ({
  onSubmit,
  initialErrors,
  modalError,
  addButtonText,
  updateButtonText,
  permit,
  statuses,
  initialValues,
  isReadOnly,
  isSimulateDeleted,
  cameFromSearch,
  handleCloseDocModal,
  callRefetch,
}: Props) => {
  const {
    handleSubmit,
    formState,
    errors,
    control,
    setValue,
    register,
    watch,
  } = useForm({
    resolver: yupResolver(
      initialValues ? schemaEdit : schema,
    ),
    defaultValues: initialValues || ({} as any),
  });

  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [commentCreated, setCommentCreated] = React.useState(null);
  const [fileSizeError, setFileSizeError] = React.useState(false);
  const [fileTypeError, setFileTypeError] = React.useState(false);
  const [apiData, setApiData] = React.useState<any>();
  const [crud, setCrud] = React.useState<CrudType>('');
  const [showConfirmDialog, _setShowConfirmDialog] = React.useState<boolean>(false);
  const [hasFile, setHasFile] = React.useState(false);
  const documentFiles = watch('documentFile');

  const setShowConfirmDialog = (what: boolean) => {
    _setShowConfirmDialog(what);
    setFileSizeError(false);
    setIsSubmitting(false);
  };
  React.useEffect(() => {
    if (initialValues && statuses) {
      if (initialValues.status) {
        const status = statuses?.find(
          el => (el?.title === initialValues.status || el?.title === initialValues?.statusName || initialValues.status === el.id),
        );

        setValue('status', status);
      }
    }
  }, [initialValues, statuses]);

  React.useEffect(() => {
    if (
      formState.isSubmitting &&
      !Object.keys(errors).length
    ) {
      setIsSubmitting(formState.isSubmitting);
    }
  }, [formState]);

  React.useEffect(() => {
    if (Object.keys(errors).length) {
      setIsSubmitting(false);
    }
  }, [errors, initialErrors]);

  const middleware = (submitFn: (data: any) => void) => (
    data: any,
  ) => {
    const _apiData = {
      id: initialValues?.pk,
      permit: +permit,
      name: data.documentName,
      status: statuses?.find(
        el => el?.title === 'Under review', // changing this as per the EA-24 requirement ---> mritunjoy
      )?.id,
      adminFile: !data?.forClient ? data.documentFile : null,
      clientFile: data?.forClient ? data.documentFile : null,
      isCompleted: data.isCompleted,
    };
    if (Object.keys(data.documentFile).length === 0) {
      setIsSubmitting(false);
      setApiData(_apiData);
      setShowConfirmDialog(true);
      setCrud('Create');
      return;
    }
    submitFn(_apiData);
  };

  const middlewareEdit = (
    submitFn: (data: any) => void,
  ) => (data: any) => {
    const _apiData = {
      id: initialValues?.pk,
      status: data.status?.id,
      adminFile: (!data?.forClient && !Array.isArray(data?.documentFile)) ? data.documentFile : null,
      clientFile: data?.forClient ? data.documentFile : null,
      isCompleted: data.isCompleted,
    };
    submitFn(_apiData);
  };

  const isPdfFile = (file: File) => {
    if (!file) return false;
    if (file.type === 'application/pdf') return true;
    return /\.pdf$/i.test(file.name || '');
  };

  const handleChange = e => {
    const _5MB = 5242880;
    if (e.target.files?.length > 0) {
      setHasFile(true);
    } else {
      setHasFile(false);
    }
    const files = Array.from((e.target.files || []) as FileList);
    const hasNonPdf = files.some(f => !isPdfFile(f));
    if (hasNonPdf) {
      setFileTypeError(true);
      setFileSizeError(false);
      return;
    }
    setFileTypeError(false);
    if (e.target.files[0]?.size > MAX_UPLOAD_FILE_SIZE) {
      setFileSizeError(true);
    } else {
      setFileSizeError(false);
      setValue('documentFile', e.target.files);
    }
  };

  const modifiedGetCommentsRequest = isSystemMsg => {
    return Promise.all([
      getPermitDocumentComments(
        initialValues?.pk,
        { ...GET_ALL_QUERY_PARAMS, system_message: isSystemMsg },
       ),
    ]).then(([response]) => {
      return {
        ...response,
        data: {
          ...response.data,
          results: response.data.results.map(comment => ({
            ...comment,
            createdAt: formatDateTime(
              comment?.createdAt,
              COMMENTS_DATETIME_FORMAT,
            ),
            creator: comment.username,
          })),
        },
      };
    });
  };

  return (
    <Container
      disabled={isSimulateDeleted}
    >
      <ConfirmNoFilesDialog
        showConfirmDialog={showConfirmDialog}
        setShowConfirmDialog={setShowConfirmDialog}
        crud={crud}
        apiData={apiData}
        handleCloseDocModal={handleCloseDocModal}
        callRefetch={callRefetch}
      />
      <FormContainer
        autoComplete="off"
        aria-autocomplete="none"
        onSubmit={handleSubmit(
          initialValues
            ? middlewareEdit(onSubmit)
            : middleware(onSubmit),
        )}
      >
        {initialValues && (
          <InputTextWithLabel
            InputProps={{ readOnly: true }}
            name="name"
            error={errors.name}
            inputRef={register}
            label={labels.name}
            isRequired={isYupFieldRequired(
              'name',
              schemaEdit,
            )}
          />
        )}
        {initialValues && (
          <InputBaseContainer>
            <Controller
              name="status"
              control={control}
              defaultValue=""
              render={props => (
                <InputSelectWithLabel
                  label={labels.status}
                  error={errors.status}
                  getOptionValue={(item: any) => item?.id}
                  getOptionLabel={(item: any) =>
                    item?.title
                  }
                  isRequired={isYupFieldRequired(
                    'status',
                    schemaEdit,
                  )}
                  isDisabled={cameFromSearch}
                  options={statuses}
                  {...props}
                />
              )}
            />
          </InputBaseContainer>
        )}
        {!initialValues && (
          <InputBaseContainer>
            <InputTextWithLabel
              name="documentName"
              error={errors.documentName}
              inputRef={register}
              label={labels.documentName}
              isRequired={isYupFieldRequired(
                'documentName',
                schema,
              )}
              disabled={isReadOnly}
            />
          </InputBaseContainer>
        )}
        {initialValues && (
          <p style={{ textAlign: 'left' }} />
        )}
        <Controller
          name="documentFile"
          control={control}
          defaultValue=""
          render={() => (
            <DropZone
              $error={!!errors.documentFile}
              $disabled={isReadOnly || cameFromSearch}
              onDragOver={e => { e.preventDefault(); e.currentTarget.setAttribute('data-drag', 'true'); }}
              onDragLeave={e => { e.currentTarget.removeAttribute('data-drag'); }}
              onDrop={e => {
                e.preventDefault();
                e.currentTarget.removeAttribute('data-drag');
                if (e.dataTransfer.files?.length) {
                  handleChange({ target: { files: e.dataTransfer.files } });
                }
              }}
            >
              <DropIcon>
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
                  <polyline points="17 8 12 3 7 8" />
                  <line x1="12" y1="3" x2="12" y2="15" />
                </svg>
              </DropIcon>
              <DropText>Drop or <DropBrowse as="label">browse<HiddenInput type="file" accept="application/pdf,.pdf" multiple onChange={handleChange} disabled={isReadOnly || cameFromSearch} /></DropBrowse> PDF files here..</DropText>
              <DropHint>PDF up to {MAX_UPLOAD_FILE_SIZE_LABEL}</DropHint>
            </DropZone>
          )}
        />
        {fileTypeError && <ErrorLine>Only PDF files are allowed.</ErrorLine>}
        {fileSizeError && (
          <ErrorLine>
            {`The file size is too large. Please upload a file below ${MAX_UPLOAD_FILE_SIZE_LABEL}.`}
          </ErrorLine>
        )}
        <FilesBubbles forControl="documentFile" documentFiles={documentFiles} setValue={setValue} />
        <InputBaseContainer>
          <Controller
            name="forClient"
            control={control}
            valueName="checked"
            defaultValue=""
            render={({ onChange, onBlur, value }) => (
              <CheckboxWithLabel
                error={errors.forClient}
                label={labels.forClient}
                checked={value}
                onBlur={onBlur}
                onChange={(e, flag) => onChange(flag)}
                disabled={!hasFile || isReadOnly || cameFromSearch}
              />
              )}
          />
        </InputBaseContainer>
        {initialValues && (
          <InputBaseContainer>
            <Controller
              name="isCompleted"
              control={control}
              valueName="checked"
              defaultValue=""
              render={({ onChange, onBlur, value }) => (
                <CheckboxWithLabel
                  error={errors.isCompleted}
                  label={labels.isCompleted}
                  checked={value}
                  onBlur={onBlur}
                  onChange={(e, flag) => onChange(flag)}
                  disabled={isReadOnly || cameFromSearch}
                />
              )}
            />
          </InputBaseContainer>
        )}
        {!isSimulateDeleted && (
        <ButtonWithLoader
          disabled={(isSubmitting && !modalError) || cameFromSearch}
          loading={isSubmitting && !modalError}
          type="submit"
        >
          {initialValues ? updateButtonText : addButtonText}
        </ButtonWithLoader>
          )}
      </FormContainer>
      {initialValues?.settingDoc && initialValues?.pk && (
        <PermitDocumentFormsTable
          settingsDoc={initialValues.settingDoc}
          id={initialValues.pk}
        />
      )}
      {initialValues && (
        <CommentsChat
          commentsRequest={modifiedGetCommentsRequest}
          entityData={initialValues}
          postRequest={createPermitDocumentComment}
          title="Notes"
          buttonText="Add Note"
          isButtonHidden={isSimulateDeleted}
          isReadOnly={cameFromSearch}
          commentCreated={commentCreated}
          setCommentCreated={setCommentCreated}
        />
      )}
    </Container>
  );
};

interface Props {
  onSubmit: (data) => void;
  initialErrors?: FormServerError<any>;
  modalError: boolean;
  addButtonText: string;
  updateButtonText: string;
  permit: number;
  statuses: Array<any>;
  initialValues?: any;
  isReadOnly :boolean;
  isSimulateDeleted? :boolean;
  cameFromSearch?:boolean;
  handleCloseDocModal?:Function;
  callRefetch: any;
}

const DropZone = styled.div<{ $error: boolean; $disabled: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--space-2);
  padding: var(--space-6) var(--space-4);
  margin: var(--space-2) 0;
  border: 2px dashed ${({ $error }) => $error ? 'var(--danger-500)' : 'var(--neutral-300)'};
  border-radius: var(--radius-md);
  background: var(--neutral-50);
  cursor: ${({ $disabled }) => $disabled ? 'not-allowed' : 'pointer'};
  opacity: ${({ $disabled }) => $disabled ? 0.5 : 1};
  transition: border-color var(--motion-fast) var(--ease-standard),
    background-color var(--motion-fast) var(--ease-standard);

  &[data-drag='true'] {
    border-color: var(--primary-600);
    background: var(--primary-50);
  }

  &:hover {
    border-color: ${({ $disabled, $error }) => $disabled ? '' : $error ? 'var(--danger-500)' : 'var(--primary-400)'};
  }
`;

const DropIcon = styled.div`
  color: var(--neutral-400);
`;

const DropText = styled.div`
  font-size: var(--fs-base);
  color: var(--neutral-600);
`;

const DropBrowse = styled.span`
  color: var(--primary-600);
  font-weight: var(--fw-semibold);
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 2px;

  &:hover {
    color: var(--primary-700);
  }
`;

const HiddenInput = styled.input`
  display: none;
`;

const DropHint = styled.div`
  font-size: var(--fs-sm);
  color: var(--neutral-400);
`;

const ErrorLine = styled.p`
  text-align: left;
  color: var(--danger-500);
  font-size: var(--fs-sm);
  margin: var(--space-1) 0;
`;

const Container = styled.fieldset`
  display: block;
  margin: 0;
  padding: 0;
  width: 100%;
  border: none;
  text-align: center;
`;

const FormContainer = styled.form`
  width: 100%;

  .MuiButton-root {
    margin-top: var(--space-4);
  }
`;

export default PermitDocumentForm;
